<script setup>
import { computed, ref, watch } from 'vue'
import MultistepAttendee from '@/components/Form/MultistepAttendee.vue'

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  name: {
    type: String,
    required: false
  },
  modelValue: {
    type: [String, Number, Boolean, Array, Object],
    required: false,
    default: ''
  }
})

const emit = defineEmits(['updateValue'])

const setValidations = () => {
  if (props.data.required) {
    return 'required'
  }
}

const fullWidthTypes = computed(() => {
  if (props.data.name === 'attendees') {
    return true
  }
  return ['textarea', 'processed_text', 'checkbox', 'radio'].includes(props.data.type)
})

const selectOptions = computed(() => {
  if (!props.data.options) {
    return []
  }

  if (Array.isArray(props.data.options)) {
    return props.data.options
  }

  return Object.entries(props.data.options).map(([value, label]) => ({
    value,
    label
  }))
})

const value = ref(props.modelValue ?? '')
const otherText = ref('')

const updateAttendee = (data) => {
  emit('updateValue', name, value)
}

watch (value, () => {
  emit('updateValue', props.name, value.value)
})

watch (otherText, () => {
  emit('updateValue', 'referrer_text', otherText.value)
})

watch(() => props.modelValue, (newValue) => {
  if (value.value !== newValue) {
    value.value = newValue ?? ''
  }
})
</script>

<template>
  <div
    v-if="data.type != 'webform_markup'"
    class="form-element relative w-full"
    :class="[
      fullWidthTypes ? 'col-span-2' : '',
      data.hidden ? 'hidden' : ''
    ]"
  >
    <FormKit
      v-if="data.type === 'checkbox'"
      :validation="data.required ? 'required' : null"
      :validation-messages="{
        required: 'Bitte Bestätigen'
      }"
      v-model="value"
      messageClass="text-danger min-h-10 text-xs mt-2"
      decoratorClass="bg-primary"
      wrapperClass="mt-5 flex flex-row items-start lg:items-center"
      inputClass="~w-5/7 ~h-5/7 bg-transparent ~mr-3/4 border-headline"
      :type="data.type"
      :name="name"
    >
      <template #label>
        <span
          class="text-subheadline ~text-xs/base"
          v-html="data.description"
        />
      </template>
    </FormKit>
    <MultistepAttendee
      v-if="data.name === 'attendees'"
      @updateValue="updateAttendee"
    />
    <FormKit
      v-else-if="data.type === 'textfield' && data.title != 'E-Mail' && !data.name.includes('referrer')"
      type="text"
      v-model="value"
      messageClass="text-primary min-h-10 text-xs mt-2"
      :label="data.title"
      :placeholder="data.required ? `${data.title} *` : data.title"
      :validation="setValidations()"
      placeholderClass="text-headline"
      inputClass="w-full bg-transparent py-3 border-b border-headline"
      labelClass="visually-hidden"
     :name="name"
    />
    <FormKit
      v-else-if="data.type === 'select' && data.name != 'referrer'"
      type="select"
      v-model="value"
      messageClass="text-danger min-h-10 text-xs mt-2"
      :label="data.title"
      :options="selectOptions"
      :placeholder="data.required ? `${data.title} *` : data.title"
      :validation="setValidations()"
      inputClass="w-full bg-transparent py-3 border-b border-headline"
      labelClass="visually-hidden"
      :name="name"
    />
    <div
      class="multi-select-form-input"
      v-else-if="data.type === 'select' && data.name == 'referrer'"
    >
      <FormKit
        type="select"
        v-model="value"
        messageClass="text-danger min-h-10 text-xs mt-2"
        :label="data.title"
        :options="selectOptions"
        :placeholder="data.required ? `${data.title} *` : data.title"
        :validation="setValidations()"
        inputClass="w-full bg-transparent py-3 border-b border-headline"
        labelClass="visually-hidden"
        :name="name"
      />
      <FormKit
        v-if="value == 'other'"
        type="text"
        v-model="otherText"
        messageClass="text-primary min-h-10 text-xs mt-6"
        label="Andere Angabe"
        placeholder="Bitte eintragen .."
        :validation="modelValue == 'other' ? 'required' : null"
        placeholderClass="text-headline"
        inputClass="w-full bg-transparent py-3 border-b border-headline"
        labelClass="visually-hidden"
        name="referrerOther"
      />
    </div>
    <FormKit
      v-else-if="data.type === 'textarea'"
      type="textarea"
      v-model="value"
      messageClass="text-danger min-h-10 text-xs mt-2"
      :label="data.title"
      :placeholder="data.title"
      :validation="setValidations()"
      inputClass="w-full p-2 rounded-md min-h-44 border-black col-span-2"
      labelClass="visually-hidden"
      :name="name"
    />
    <FormKit
      v-else-if="data.type === 'email' || data.title === 'E-Mail'"
      type="email"
      v-model="value"
      messageClass="text-danger min-h-10 text-xs mt-2"
      :label="data.title"
      :placeholder="data.required ? `${data.title} *` : data.title"
      validation="required|email"
      inputClass="w-full bg-transparent py-3 border-b border-headline"
      placeholderClass="text-headline"
      labelClass="visually-hidden"
      :name="name"
    />
  </div>
</template>

<style lang="scss" scoped>
  .form-element {
    :deep(ul) {
      @apply mb-0;
    }
    :deep(p a) {
      @apply underline text-primary;
    }

    :deep(input), :deep(textarea) {
      &::placeholder {
        @apply text-headline opacity-60;
      }
    }

    :deep(input[type="text"]), :deep(select) {
      &:focus {
        @apply ring-black border-headline;
      }
    }

    :deep(input[type="checkbox"]) {
      &:focus {
        @apply ring-black;
      }
      &:checked {
        @apply bg-primary;
      }
    }
  }
</style>
